import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PreviewQuestionLabel from "../../../common/PreviewQuestionLabel/PreviewQuestionLabel";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useSurveyInputStore } from "../../../../surveyStore";
import PreviewQuestionConstraints from "../../../common/PreviewQuestionConstraints/PreviewQuestionConstraints";
import useEvaluateConstraint from "../../question-preview/hooks/useEvaluateConstraint";
import useEvaluateAppearance from "../../question-preview/hooks/useEvaluateAppearance";

import "./Date.scss";

dayjs.extend(customParseFormat);

export const Date = React.memo(
   ({ props, hasErrors, surveyId, userId, submissionId, label, hint, defaultValue }) => {
      const datePickerRef = useRef(null);

      const { id, questionType, isRequired, answers, name } = props;

      const inputState = useSurveyInputStore((state) => state.inputs?.[id]);
      const setInputs = useSurveyInputStore((state) => state.setInputs);

      const hasSubmissionId = Boolean(submissionId);

      const submissionAnswer = hasSubmissionId
         ? answers?.find((answer) =>
              props.repeatInstanceIndex
                 ? answer?.answerDetail.repeatGroup === props.repeatInstanceIndex
                 : answer
           )?.answerDetail?.text
         : null;

      const value =
         hasSubmissionId && submissionAnswer
            ? dayjs(submissionAnswer, "YYYY-MM:DD")
            : inputState
            ? dayjs(inputState?.answerDetail?.text, "YYYY-MM:DD")
            : "";

      const passConstraint = useEvaluateConstraint(
         !hasSubmissionId ? props.constraint : null,
         inputState?.skipLogicAnswer
      );

      const appearance = useEvaluateAppearance({
         appearance: props.appearance
      });

      const showErrors =
         hasErrors && isRequired && (!inputState || !inputState?.answerDetail?.text);

      const handleFocus = () => {
         const input = datePickerRef.current?.input;
         if (input) {
            input.focus({ preventScroll: true });
         }
      };

      const onDatePickerChange = useCallback(
         (date, dataString) => {
            if (hasSubmissionId) {
               return;
            }
            if (date) {
               let processedDate = date;
               let processedDataString = dataString;

               if (appearance.includes("month-year")) {
                  processedDate = date.startOf("month");
                  processedDataString = processedDate.format("YYYY-MM-01");
               }

               setInputs({
                  target: {
                     name: id,
                     value: {
                        surveyId: surveyId,
                        userId: userId,
                        questionId: id,
                        questionType: questionType,
                        name: name,
                        skipLogicAnswer: processedDate.startOf("day"),
                        answerDetail: {
                           text: processedDataString
                        }
                     }
                  }
               });
            } else {
               setInputs({ target: { name: id, value: "" } });
            }
         },
         [appearance, hasSubmissionId, id, name, questionType, setInputs, surveyId, userId]
      );

      const memoDatePicker = useMemo(() => {
         return (
            <DatePicker
               ref={datePickerRef}
               onFocus={handleFocus}
               onChange={onDatePickerChange}
               placeholder={appearance.includes("month-year") ? "YYYY-MM" : "YYYY-MM-DD"}
               picker={appearance.includes("month-year") ? "month" : "date"}
               inputReadOnly={hasSubmissionId}
               {...(hasSubmissionId && { open: false })}
               status={showErrors || (passConstraint !== null && !passConstraint) ? "error" : ""}
               value={value}
               size="large"
               prefixCls="date-submission-type-wrapper--date-picker"
            />
         );
      }, [appearance, hasSubmissionId, onDatePickerChange, passConstraint, showErrors, value]);

      useEffect(() => {
         if (defaultValue) {
            let normalizedDate;

            // if (value) {
            //    normalizedDate = dayjs(value.$d.$d);
            //    return;
            // }
            if (defaultValue?.$d?.$d) {
               normalizedDate = dayjs(defaultValue.$d.$d);
            } else {
               normalizedDate = defaultValue?.$isDayjsObject ? defaultValue : dayjs(props.default);
            }

            onDatePickerChange(normalizedDate, normalizedDate.format("YYYY-MM-DD"));
         }
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [defaultValue, props.default]);

      return (
         <div className="date-submission-type-wrapper">
            <PreviewQuestionLabel label={label} isRequired={isRequired} hint={hint} />

            {memoDatePicker}

            <span className="date-submission-type-wrapper--error">
               {showErrors ? "This field is required" : " "}
               {passConstraint !== null && !passConstraint ? (
                  <PreviewQuestionConstraints constraintMessages={props.constraintMessages} />
               ) : null}
            </span>
         </div>
      );
   }
);
